import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUpcomingMeetings } from 'slices/availability/thunk';
import { Dropdown } from 'Common/Components/Dropdown';
import { Calendar, Clock, Video } from 'lucide-react';
import moment from 'moment';

const UpcomingMeetings = () => {
    const dispatch = useDispatch<any>();
    const { upcomingMeetings, loading } = useSelector((state: any) => state.Availability);

    useEffect(() => {
        dispatch(getUpcomingMeetings(5));
        return () => {};
    }, [dispatch]);

    const formatTime = (dateString: string) => {
        try {
            return moment(dateString).format('h:mm A');
        } catch (error) {
            return '';
        }
    };

    const formatDate = (dateString: string) => {
        try {
            const meetingDate = moment(dateString);
            const now = moment();
            
            if (meetingDate.isSame(now, 'day')) {
                return 'Today';
            } else if (meetingDate.isSame(now.add(1, 'day'), 'day')) {
                return 'Tomorrow';
            } else {
                return meetingDate.format('MMM D');
            }
        } catch (error) {
            return '';
        }
    };

    const getNextMeeting = () => {
        if (!upcomingMeetings || upcomingMeetings.length === 0) return null;
        return upcomingMeetings[0];
    };

    const nextMeeting = getNextMeeting();

    if (!nextMeeting && !loading) {
        return null;
    }

    return (
        <Dropdown className="relative">
            <Dropdown.Trigger
                type="button"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
                <Calendar className="w-4 h-4" />
                {nextMeeting && (
                    <>
                        <span className="hidden sm:inline">
                            {formatTime(nextMeeting.start_date)}
                        </span>
                        <span className="sm:hidden">Meeting</span>
                    </>
                )}
                {upcomingMeetings && upcomingMeetings.length > 1 && (
                    <span className="flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-500 rounded-full">
                        {upcomingMeetings.length}
                    </span>
                )}
            </Dropdown.Trigger>
            <Dropdown.Content 
                placement="bottom-end"
                className="absolute z-50 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2"
                style={{ right: '0', left: 'auto' }}
            >
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        Upcoming Meetings
                    </h3>
                </div>
                {loading ? (
                    <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                        Loading...
                    </div>
                ) : upcomingMeetings && upcomingMeetings.length > 0 ? (
                    <div className="max-h-96 overflow-y-auto">
                        {upcomingMeetings.map((meeting: any) => (
                            <div
                                key={meeting.id}
                                className="px-4 py-3 border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors last:border-b-0"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                            {meeting.title}
                                        </h4>
                                        {meeting.description && (
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                                                {meeting.description}
                                            </p>
                                        )}
                                        <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                <span>{formatDate(meeting.start_date)}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatTime(meeting.start_date)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {meeting.meeting_link && (
                                        <a
                                            href={meeting.meeting_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                            title="Join Meeting"
                                        >
                                            <Video className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                        No upcoming meetings
                    </div>
                )}
            </Dropdown.Content>
        </Dropdown>
    );
};

export default UpcomingMeetings;

